import { useState } from "react";
import { NavLink } from "react-router";
import { clsx } from "clsx";
import { BookOpen, Plus } from "lucide-react";
import { ReglementDialog } from "@/features/sponsorship/components/reglement/ReglementDialog";
import { SPONSORSHIP_CTA_CREATE_CLASS } from "@/features/sponsorship/components/sponsorshipCta.styles";
import {
  SPONSORSHIP_CONVERSIONS_PATH,
  SPONSORSHIP_HOW_IT_WORKS_PATH,
  SPONSORSHIP_PATH,
  SPONSORSHIP_RECOMMENDATIONS_PATH,
} from "@/features/sponsorship/constants/paths";
import { useRecommendationModal } from "@/features/sponsorship/context/RecommendationModalContext";

type TabItem = {
  to: string
  label: string
  end?: boolean
};

const baseClass =
  "inline-flex h-9 items-center rounded-full px-4 text-sm font-semibold transition-colors";

const idleClass =
  "text-gray-700 hover:bg-violet-50 hover:text-violet-800 dark:text-gray-300 dark:hover:bg-violet-950/60 dark:hover:text-violet-200";

const activeClass =
  "bg-violet-600 text-white shadow-sm hover:bg-violet-700";

const reglementButtonClass = clsx(
  baseClass,
  "gap-2 text-gray-700 hover:bg-violet-50 hover:text-violet-800",
  "dark:text-gray-300 dark:hover:bg-violet-950/60 dark:hover:text-violet-200",
);

const TABS: TabItem[] = [
  { to: SPONSORSHIP_PATH, label: "Aperçu", end: true },
  { to: SPONSORSHIP_RECOMMENDATIONS_PATH, label: "Mes recommandations" },
  { to: SPONSORSHIP_CONVERSIONS_PATH, label: "Mes conversions" },
  { to: SPONSORSHIP_HOW_IT_WORKS_PATH, label: "Comment ça marche" },
];

export const SponsorshipTabs = () => {
  const [isReglementOpen, setIsReglementOpen] = useState(false);
  const { openRecommendationModal, canRecommend } = useRecommendationModal();

  return (
    <div className="flex items-center justify-between gap-2">
      <nav
        aria-label="Sous-navigation parrainage"
        className="min-w-0 flex-1 overflow-x-auto"
      >
        <ul className="flex items-center gap-2 whitespace-nowrap pb-1">
          {TABS.map((tab) => (
            <li key={tab.to}>
              <NavLink
                to={tab.to}
                end={tab.end}
                className={({ isActive }) =>
                  clsx(baseClass, isActive ? activeClass : idleClass)}
              >
                {tab.label}
              </NavLink>
            </li>
          ))}
          {/* CTA de création présenté comme un onglet, juste après « Comment ça
              marche ». Design pill gradient partagé, mais libellé + icône
              distincts du bouton du header (qui, lui, navigue) : ici on ouvre
              le formulaire, d'où « + Nouvelle recommandation ». */}
          {canRecommend && (
            <li>
              <button
                type="button"
                onClick={openRecommendationModal}
                aria-label="Nouvelle recommandation"
                className={clsx(SPONSORSHIP_CTA_CREATE_CLASS, "h-9 shrink-0")}
              >
                <Plus size={16} className="relative z-[1]" aria-hidden />
                <span className="relative z-[1]">Nouvelle recommandation</span>
              </button>
            </li>
          )}
        </ul>
      </nav>
      <button
        type="button"
        onClick={() => setIsReglementOpen(true)}
        aria-label="Voir le règlement du Programme Yeati Blue"
        className={reglementButtonClass}
      >
        <BookOpen size={16} aria-hidden />
        <span className="hidden sm:inline">Règlement</span>
      </button>
      <ReglementDialog open={isReglementOpen} onClose={() => setIsReglementOpen(false)} />
    </div>
  );
};

SponsorshipTabs.displayName = "SponsorshipTabs";
